/**
 * Módulo principal de la aplicación Landing Sintracajamag
 * Maneja la carga de datos desde la API de forma segura
 * @version 2.0.0
 * @author Sintracajamag Dev Team
 */

(function() {
    'use strict';
    
    // ==================== CONFIGURACIÓN ====================
    const CONFIG = {
        API_URL: (document.location.hostname === '127.0.0.1' || document.location.hostname === 'localhost') 
            ? 'http://localhost:5075/api' 
            : 'https://api.sintracajamag.com.co',
        RETRY_ATTEMPTS: 3,
        RETRY_DELAY: 1000, // ms
        CACHE_DURATION: 5 * 60 * 1000, // 5 minutos
        TIMEOUT: 10000 // 10 segundos
    };

    // Cache simple en memoria
    const cache = new Map();

    // ==================== UTILIDADES DE SEGURIDAD ====================
    
    /**
     * Sanitiza texto para prevenir XSS
     * @param {string} text - Texto a sanitizar
     * @returns {string} Texto sanitizado
     */
    function sanitizeHTML(text) {
        if (!text) return '';
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Valida URL de imagen
     * @param {string} url - URL a validar
     * @returns {string} URL validada o imagen por defecto
     */
    function validateImageURL(url) {
        if (!url || typeof url !== 'string') {
            return 'assets/img/default-avatar.png';
        }
        
        // Solo permitir imágenes del mismo origen o rutas relativas
        if (url.startsWith('/') || url.startsWith('./') || url.startsWith('assets/')) {
            return url;
        }
        
        return 'assets/img/default-avatar.png';
    }

    /**
     * Valida email básico
     * @param {string} email - Email a validar
     * @returns {boolean} True si es válido
     */
    function isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    // ==================== FUNCIONES DE API ====================

    /**
     * Realiza petición HTTP con timeout y reintentos
     * @param {string} url - URL del endpoint
     * @param {object} options - Opciones de fetch
     * @param {number} retries - Número de reintentos restantes
     * @returns {Promise<Response>} Respuesta HTTP
     */
    async function fetchWithTimeout(url, options = {}, retries = CONFIG.RETRY_ATTEMPTS) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), CONFIG.TIMEOUT);

        try {
            const response = await fetch(url, {
                ...options,
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return response;
        } catch (error) {
            clearTimeout(timeoutId);

            if (retries > 0 && error.name !== 'AbortError') {
                console.warn(`Reintentando petición... Intentos restantes: ${retries}`);
                await new Promise(resolve => setTimeout(resolve, CONFIG.RETRY_DELAY));
                return fetchWithTimeout(url, options, retries - 1);
            }

            throw error;
        }
    }

    /**
     * Obtiene datos de un controlador de la API con caché
     * @param {string} controller - Nombre del controlador
     * @returns {Promise<Array>} Datos del controlador
     */
    async function getData(controller) {
        // Validar entrada
        if (!controller || typeof controller !== 'string') {
            throw new Error('Nombre de controlador inválido');
        }

        // Verificar caché
        const cacheKey = `${controller}_${Date.now()}`;
        const cached = getCachedData(controller);
        if (cached) {
            console.log(`Usando datos en caché para: ${controller}`);
            return cached;
        }

        const url = `${CONFIG.API_URL}/${controller}`;
        
        try {
            const response = await fetchWithTimeout(url, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache',
                credentials: 'omit', // No enviar cookies por seguridad
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                referrerPolicy: 'no-referrer'
            });

            const data = await response.json();
            
            // Validar que sea un array o un objeto
            if (!Array.isArray(data) && typeof data !== 'object') {
                throw new Error('Formato de datos inválido');
            }

            // Guardar en caché
            setCachedData(controller, data);
            
            return data;
        } catch (error) {
            console.error(`Error al obtener datos de ${controller}:`, error);
            
            // Si hay datos en caché antiguos, usarlos como fallback
            const staleCache = cache.get(controller);
            if (staleCache) {
                console.warn('Usando caché antiguo como fallback');
                return staleCache.data;
            }
            
            throw error;
        }
    }

    /**
     * Guarda datos en caché con timestamp
     * @param {string} key - Clave del caché
     * @param {*} data - Datos a cachear
     */
    function setCachedData(key, data) {
        cache.set(key, {
            data,
            timestamp: Date.now()
        });
    }

    /**
     * Obtiene datos del caché si no están expirados
     * @param {string} key - Clave del caché
     * @returns {*|null} Datos en caché o null
     */
    function getCachedData(key) {
        const cached = cache.get(key);
        if (!cached) return null;

        const age = Date.now() - cached.timestamp;
        if (age > CONFIG.CACHE_DURATION) {
            cache.delete(key);
            return null;
        }

        return cached.data;
    }

    // ==================== RENDERIZADO DE UI ====================

    /**
     * Crea el HTML de un directivo de forma segura
     * @param {object} item - Datos del directivo
     * @returns {string} HTML sanitizado
     */
    function createDirectivoHTML(item) {
        // Validar y sanitizar todos los campos
        const nombre = sanitizeHTML(item.nombre || 'Sin nombre');
        const cargo = sanitizeHTML(item.cargo || 'Sin cargo');
        const profesion = sanitizeHTML(item.profesion || 'Sin profesión');
        const correo = isValidEmail(item.correo) ? sanitizeHTML(item.correo) : '';
        const foto = validateImageURL(item.foto);

        return `
            <div class="swiper-slide">
                <div class="testimonial-item">
                    <p>
                        <i class="bx bxs-quote-alt-left quote-icon-left"></i>
                        ${nombre}
                        <i class="bx bxs-quote-alt-right quote-icon-right"></i>
                    </p>
                    <img src="${foto}" 
                         class="testimonial-img" 
                         alt="Foto de ${nombre}"
                         loading="lazy"
                         onerror="this.src='assets/img/default-avatar.jpg'">
                    <h3>${cargo}</h3>
                    <h4>${profesion}</h4>
                    ${correo ? `<h6><a href="mailto:${correo}" aria-label="Enviar correo a ${nombre}">${correo}</a></h6>` : ''}
                </div>
            </div>
        `;
    }

    /**
     * Muestra mensaje de error en la UI
     * @param {string} containerId - ID del contenedor
     * @param {string} message - Mensaje de error
     */
    function showError(containerId, message) {
        const container = document.getElementById(containerId);
        if (!container) return;

        container.innerHTML = `
            <div class="alert alert-warning text-center" role="alert">
                <i class="bi bi-exclamation-triangle"></i>
                <p class="mb-0">${sanitizeHTML(message)}</p>
            </div>
        `;
    }

    /**
     * Muestra indicador de carga
     * @param {string} containerId - ID del contenedor
     */
    function showLoading(containerId) {
        const container = document.getElementById(containerId);
        if (!container) return;

        container.innerHTML = `
            <div class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Cargando...</span>
                </div>
                <p class="mt-3">Cargando información...</p>
            </div>
        `;
    }

    /**
     * Carga y renderiza los directivos
     */
    async function loadDirectivos() {
        const container = document.getElementById('swiper-wrapper');
        if (!container) {
            console.error('Contenedor swiper-wrapper no encontrado');
            return;
        }

        try {
            showLoading('swiper-wrapper');
            
            const data = await getData('Directivos');
            
            if (!data || data.length === 0) {
                showError('swiper-wrapper', 'No hay directivos disponibles en este momento.');
                return;
            }

            // Generar HTML de forma segura
            const html = data.map(item => createDirectivoHTML(item)).join('');
            container.innerHTML = html;

            // Reinicializar Swiper si existe
            if (window.Swiper && typeof initSwiper === 'function') {
                setTimeout(() => initSwiper(), 100);
            }

            console.log(`✅ ${data.length} directivos cargados exitosamente`);
        } catch (error) {
            console.error('Error al cargar directivos:', error);
            showError('swiper-wrapper', 
                'No se pudieron cargar los datos. Por favor, intente nuevamente más tarde.');
        }
    }

    // ==================== EVENTOS ====================

    /**
     * Crea HTML seguro para un evento
     * @param {object} evento - Datos del evento
     * @returns {string} HTML del evento
     */
    function createEventoHTML(evento) {
        const titulo = sanitizeHTML(evento.titulo || 'Sin título');
        const descripcion = sanitizeHTML(evento.descripcion || 'Sin descripción');
        const lugar = sanitizeHTML(evento.lugar || 'Lugar no especificado');
        const ciudad = sanitizeHTML(evento.ciudad || 'Ciudad no especificada');
        const imagen = validateImageURL(evento.imagenUrl);
        const descripcionResumida = descripcion.length > 150
            ? descripcion.substring(0, 150) + '...'
            : descripcion;

        // Formatear fecha
        let fechaTexto = 'Fecha no disponible';
        if (evento.fechaRealizacion) {
            try {
                const fecha = new Date(evento.fechaRealizacion);
                fechaTexto = fecha.toLocaleDateString('es-CO', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            } catch (e) {
                console.warn('Error al formatear fecha del evento:', e);
            }
        }

        return `
            <div class="col-lg-6 col-md-6 mb-4">
                <div class="icon-box evento-card" data-aos="zoom-in-left">
                    ${imagen !== 'assets/img/default-avatar.png' ? `
                        <img src="${imagen}" 
                             alt="${titulo}" 
                             loading="lazy" 
                             width="350" 
                             height="233"
                             onerror="this.src='assets/img/default-event.jpg'"
                             style="border-radius: 5px; margin-bottom: 15px; width: 100%; height: auto; object-fit: cover;">
                    ` : ''}
                    <h4 class="title">
                        <a href="#" 
                           onclick="window.SintracajamagApp.mostrarEvento(${evento.id}); return false;" 
                           aria-label="Ver detalles del evento">
                            ${titulo}
                        </a>
                    </h4>
                    <p class="description">
                        <strong>📍 ${lugar}, ${ciudad}</strong>
                        <br><strong>📅 ${fechaTexto}</strong>
                        <br><br>${descripcionResumida}
                        <br><a href="#" 
                               class="btn-get-started animate__animated animate__fadeInUp scrollto" 
                               onclick="window.SintracajamagApp.mostrarEvento(${evento.id}); return false;"
                               aria-label="Ver más detalles sobre ${titulo}">
                            Ver Evento Completo
                        </a>
                    </p>
                </div>
            </div>
        `;
    }

    /**
     * Muestra un evento completo en un modal
     * @param {number} eventoId - ID del evento
     */
    async function mostrarEvento(eventoId) {
        try {
            const evento = await getData(`Eventos/${eventoId}`);
            
            const titulo = sanitizeHTML(evento.titulo);
            const descripcion = sanitizeHTML(evento.descripcion).replace(/\n/g, '<br>');
            const lugar = sanitizeHTML(evento.lugar);
            const ciudad = sanitizeHTML(evento.ciudad);
            const imagen = validateImageURL(evento.imagenUrl);

            let fechaTexto = 'Fecha no disponible';
            if (evento.fechaRealizacion) {
                try {
                    const fecha = new Date(evento.fechaRealizacion);
                    fechaTexto = fecha.toLocaleDateString('es-CO', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    });
                } catch (e) {
                    console.warn('Error al formatear fecha:', e);
                }
            }

            const modalHTML = `
                <div class="modal fade" id="eventoModal" tabindex="-1" role="dialog" aria-hidden="true">
                    <div class="modal-dialog modal-lg" role="document">
                        <div class="modal-content">
                            <div class="modal-header">
                                <h5 class="modal-title">${titulo}</h5>
                                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
                            </div>
                            <div class="modal-body">
                                ${imagen !== 'assets/img/default-avatar.png' ? `
                                    <img src="${imagen}" 
                                         alt="${titulo}" 
                                         class="img-fluid mb-3"
                                         onerror="this.src='assets/img/default-event.jpg'">
                                ` : ''}
                                <div class="mb-3">
                                    <h6><strong>📍 Lugar:</strong> ${lugar}, ${ciudad}</h6>
                                    <h6><strong>📅 Fecha y Hora:</strong> ${fechaTexto}</h6>
                                </div>
                                <div>
                                    <h6><strong>Descripción:</strong></h6>
                                    <p>${descripcion}</p>
                                </div>
                            </div>
                            <div class="modal-footer">
                                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            `;

            // Remover modal anterior si existe
            const existingModal = document.getElementById('eventoModal');
            if (existingModal) {
                existingModal.remove();
            }

            document.body.insertAdjacentHTML('beforeend', modalHTML);
            const modal = new bootstrap.Modal(document.getElementById('eventoModal'));
            modal.show();

            // Limpiar modal al cerrar
            document.getElementById('eventoModal').addEventListener('hidden.bs.modal', () => {
                document.getElementById('eventoModal').remove();
            });

        } catch (error) {
            console.error('Error al mostrar evento:', error);
            alert('No se pudo cargar el evento. Por favor intente nuevamente.');
        }
    }

    /**
     * Carga y renderiza los eventos publicados
     */
    async function loadEventos() {
        const container = document.querySelector('#eventos .row');
        if (!container) {
            console.error('Contenedor de eventos no encontrado');
            return;
        }

        try {
            // Mostrar loading
            container.innerHTML = `
                <div class="col-12 text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Cargando...</span>
                    </div>
                    <p class="mt-3">Cargando información...</p>
                </div>
            `;
            
            const data = await getData('Eventos/ultimos?cantidad=4');
            
            if (!data || data.length === 0) {
                container.innerHTML = `
                    <div class="col-12 text-center">
                        <p class="text-muted">No hay eventos disponibles en este momento.</p>
                    </div>
                `;
                return;
            }

            // Generar HTML de forma segura
            const html = data.map(item => createEventoHTML(item)).join('');
            container.innerHTML = html;

            console.log(`✅ ${data.length} eventos cargados exitosamente`);
        } catch (error) {
            console.error('Error al cargar eventos:', error);
            container.innerHTML = `
                <div class="col-12 text-center">
                    <p class="text-danger">No se pudieron cargar los eventos. Por favor, intente nuevamente más tarde.</p>
                </div>
            `;
        }
    }

    // ==================== COMUNICADOS ====================

    /**
     * Crea HTML seguro para un comunicado
     * @param {object} comunicado - Datos del comunicado
     * @returns {string} HTML del comunicado
     */
    function createComunicadoHTML(comunicado) {
        const titulo = sanitizeHTML(comunicado.titulo || 'Sin título');
        const contenido = sanitizeHTML(comunicado.contenido || 'Sin contenido');
        const contenidoResumido = contenido.length > 200 
            ? contenido.substring(0, 200) + '...' 
            : contenido;
        
        // Formatear fecha
        let fechaTexto = 'Fecha no disponible';
        if (comunicado.fechaPublicacion) {
            try {
                const fecha = new Date(comunicado.fechaPublicacion);
                fechaTexto = fecha.toLocaleDateString('es-CO', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            } catch (e) {
                console.warn('Error al formatear fecha:', e);
            }
        }

        return `
            <div class="col-lg-4 col-md-6 d-flex align-items-stretch mb-5">
                <div class="icon-box">
                    <div class="icon"><i class="bx bxs-file-doc"></i></div>
                    <h4>
                        <a href="#" onclick="event.preventDefault(); window.SintracajamagApp.mostrarComunicado('${comunicado.id}')" 
                           style="color: inherit; text-decoration: none;">
                            ${titulo}
                        </a>
                    </h4>
                    <p style="font-size: 0.875rem; color: #666; margin-bottom: 0.5rem;">
                        <i class="bx bx-calendar"></i> ${fechaTexto}
                    </p>
                    ${comunicado.lugar ? `
                        <p style="font-size: 0.875rem; color: #666; margin-bottom: 0.5rem;">
                            <i class="bx bx-map"></i> ${sanitizeHTML(comunicado.lugar)}
                        </p>
                    ` : ''}
                    <p>${contenidoResumido}</p>
                    <button class="btn btn-sm btn-outline-primary" 
                            onclick="window.SintracajamagApp.mostrarComunicado('${comunicado.id}')" 
                            style="border: none; color: #0d47a1; cursor: pointer; text-decoration: underline;">
                        Leer más →
                    </button>
                </div>
            </div>
        `;
    }

    /**
     * Muestra los detalles de un comunicado en un modal
     * @param {string} id - ID del comunicado
     */
    async function mostrarComunicado(id) {
        try {
            // Validar ID
            if (!id || typeof id !== 'string') {
                throw new Error('ID de comunicado inválido');
            }

            // Obtener datos del comunicado
            const url = `${CONFIG.API_URL}/Comunicados/${id}`;
            const response = await fetchWithTimeout(url, {
                method: 'GET',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const comunicado = await response.json();
            
            // Formatear fechas
            let fechaPublicacion = 'Fecha no disponible';
            let fechaRealizacion = '';

            if (comunicado.fechaPublicacion) {
                try {
                    const fecha = new Date(comunicado.fechaPublicacion);
                    fechaPublicacion = fecha.toLocaleDateString('es-CO', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    });
                } catch (e) {
                    console.warn('Error al formatear fecha:', e);
                }
            }

            if (comunicado.fechaRealizacion) {
                try {
                    const fecha = new Date(comunicado.fechaRealizacion);
                    fechaRealizacion = fecha.toLocaleDateString('es-CO', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    });
                } catch (e) {
                    console.warn('Error al formatear fecha:', e);
                }
            }

            // Crear modal
            let modalHTML = `
                <div class="modal fade" id="comunicadoModal" tabindex="-1" aria-hidden="true">
                    <div class="modal-dialog modal-lg">
                        <div class="modal-content">
                            <div class="modal-header">
                                <h5 class="modal-title">${sanitizeHTML(comunicado.titulo)}</h5>
                                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                            </div>
                            <div class="modal-body">
                                <p style="color: #666; margin-bottom: 1rem;">
                                    <i class="bx bx-calendar"></i> <strong>Publicado:</strong> ${fechaPublicacion}
                                </p>
            `;

            if (comunicado.lugar) {
                modalHTML += `
                    <p style="color: #666; margin-bottom: 1rem;">
                        <i class="bx bx-map"></i> <strong>Lugar:</strong> ${sanitizeHTML(comunicado.lugar)}
                    </p>
                `;
            }

            if (fechaRealizacion) {
                modalHTML += `
                    <p style="color: #666; margin-bottom: 1rem;">
                        <i class="bx bx-calendar-event"></i> <strong>Fecha de realización:</strong> ${fechaRealizacion}
                    </p>
                `;
            }

            modalHTML += `
                                <div style="margin-top: 1.5rem; line-height: 1.6;">
                                    ${sanitizeHTML(comunicado.contenido)}
                                </div>
            `;

            if (comunicado.documentoPdf) {
                modalHTML += `
                    <div style="margin-top: 1.5rem;">
                        <a href="${sanitizeHTML(comunicado.documentoPdf)}" 
                           target="_blank" 
                           rel="noopener noreferrer"
                           class="btn btn-sm btn-outline-primary">
                            <i class="bx bxs-file-pdf"></i> Descargar PDF
                        </a>
                    </div>
                `;
            }

            modalHTML += `
                            </div>
                            <div class="modal-footer">
                                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            `;

            // Remover modal anterior si existe
            const oldModal = document.getElementById('comunicadoModal');
            if (oldModal) {
                oldModal.remove();
            }

            // Agregar modal al DOM
            document.body.insertAdjacentHTML('beforeend', modalHTML);

            // Mostrar modal
            const modal = new bootstrap.Modal(document.getElementById('comunicadoModal'));
            modal.show();

            console.log('✅ Comunicado mostrado exitosamente');
        } catch (error) {
            console.error('Error al mostrar comunicado:', error);
            alert('No se pudo cargar el comunicado. Por favor intente nuevamente.');
        }
    }

    /**
     * Carga y renderiza los comunicados publicados
     */
    async function loadComunicados() {
        const container = document.querySelector('#services .row');
        if (!container) {
            console.error('Contenedor de comunicados no encontrado');
            return;
        }

        try {
            // Mostrar loading
            container.innerHTML = `
                <div class="col-12 text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Cargando...</span>
                    </div>
                    <p class="mt-3">Cargando información...</p>
                </div>
            `;
            
            const data = await getData('Comunicados/ultimos?cantidad=6');
            
            if (!data || data.length === 0) {
                container.innerHTML = `
                    <div class="col-12 text-center">
                        <p class="text-muted">No hay comunicados disponibles en este momento.</p>
                    </div>
                `;
                return;
            }

            // Generar HTML de forma segura
            const html = data.map(item => createComunicadoHTML(item)).join('');
            container.innerHTML = html;

            console.log(`✅ ${data.length} comunicados cargados exitosamente`);
        } catch (error) {
            console.error('Error al cargar comunicados:', error);
            container.innerHTML = `
                <div class="col-12 text-center">
                    <p class="text-danger">No se pudieron cargar los comunicados. Por favor, intente nuevamente más tarde.</p>
                </div>
            `;
        }
    }

    // ==================== NOTICIAS ====================

    /**
     * Crea HTML seguro para una noticia

     * @param {object} noticia - Datos de la noticia
     * @returns {string} HTML de la noticia
     */
    function createNoticiaHTML(noticia) {
        const titulo = sanitizeHTML(noticia.titulo || 'Sin título');
        const contenido = sanitizeHTML(noticia.contenido || 'Sin contenido');
        const autor = sanitizeHTML(noticia.autor || 'Desconocido');
        const imagen = validateImageURL(noticia.imagen);
        const contenidoResumido = contenido.length > 200 
            ? contenido.substring(0, 200) + '...' 
            : contenido;
        
        // Formatear fecha
        let fechaTexto = 'Fecha no disponible';
        if (noticia.fechaPublicacion) {
            try {
                const fecha = new Date(noticia.fechaPublicacion);
                fechaTexto = fecha.toLocaleDateString('es-CO', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            } catch (e) {
                console.warn('Error al formatear fecha:', e);
            }
        }

        return `
            <div class="col-lg-4 col-md-6 mb-4">
                <div class="icon-box noticia-card" data-aos="zoom-in-left">
                    <img src="${imagen}" 
                         alt="${titulo}" 
                         loading="lazy" 
                         width="350" 
                         height="233"
                         onerror="this.src='assets/img/default-news.jpg'">
                    <h4 class="title">
                        <a href="#" 
                           onclick="window.SintracajamagApp.mostrarNoticia('${noticia.id}'); return false;" 
                           aria-label="Leer noticia completa">
                            ${titulo}
                        </a>
                    </h4>
                    <p class="description">
                        ${contenidoResumido}
                        <br><small class="text-muted">${fechaTexto} • Por ${autor}</small>
                        <br><a href="#" 
                               class="btn-get-started animate__animated animate__fadeInUp scrollto" 
                               onclick="window.SintracajamagApp.mostrarNoticia('${noticia.id}'); return false;"
                               aria-label="Leer más sobre ${titulo}">
                            Leer más
                        </a>
                    </p>
                </div>
            </div>
        `;
    }

    /**
     * Muestra una noticia completa en un modal
     * @param {string} noticiaId - ID de la noticia
     */
    async function mostrarNoticia(noticiaId) {
        try {
            const noticia = await getData(`Noticias/${noticiaId}`);
            
            if (!noticia) {
                alert('No se pudo cargar la noticia. Por favor, intente nuevamente.');
                return;
            }

            const titulo = sanitizeHTML(noticia.titulo || 'Sin título');
            const contenido = sanitizeHTML(noticia.contenido || 'Sin contenido').replace(/\n/g, '<br>');
            const autor = sanitizeHTML(noticia.autor || 'Desconocido');
            const imagen = validateImageURL(noticia.imagen);

            let fechaTexto = 'Fecha no disponible';
            if (noticia.fechaPublicacion) {
                try {
                    const fecha = new Date(noticia.fechaPublicacion);
                    fechaTexto = fecha.toLocaleDateString('es-CO', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    });
                } catch (e) {
                    console.warn('Error al formatear fecha:', e);
                }
            }

            // Crear o obtener modal
            let modal = document.getElementById('noticiaModal');
            if (!modal) {
                modal = document.createElement('div');
                modal.id = 'noticiaModal';
                modal.className = 'modal fade';
                modal.setAttribute('tabindex', '-1');
                modal.setAttribute('role', 'dialog');
                document.body.appendChild(modal);
            }

            modal.innerHTML = `
                <div class="modal-dialog modal-lg" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">${titulo}</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
                        </div>
                        <div class="modal-body">
                            <img src="${imagen}" 
                                 alt="${titulo}" 
                                 class="img-fluid mb-3"
                                 onerror="this.src='assets/img/default-news.jpg'">
                            <p class="text-muted"><small>${fechaTexto} • Por ${autor}</small></p>
                            <div class="noticia-contenido">${contenido}</div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cerrar</button>
                        </div>
                    </div>
                </div>
            `;

            // Mostrar modal usando Bootstrap
            if (typeof bootstrap !== 'undefined') {
                const bsModal = new bootstrap.Modal(modal);
                bsModal.show();
            }
        } catch (error) {
            console.error('Error al mostrar noticia:', error);
            alert('No se pudo cargar la noticia. Por favor, intente nuevamente.');
        }
    }

    /**
     * Carga y renderiza las noticias publicadas
     */
    async function loadNoticias() {
        const container = document.querySelector('#noticias .row');
        if (!container) {
            console.error('Contenedor de noticias no encontrado');
            return;
        }

        try {
            // Mostrar loading directamente en el contenedor
            container.innerHTML = `
                <div class="col-12 text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Cargando...</span>
                    </div>
                    <p class="mt-3">Cargando información...</p>
                </div>
            `;
            
            const data = await getData('Noticias/publicadas?cantidad=10');
            
            if (!data || data.length === 0) {
                container.innerHTML = `
                    <div class="col-12 text-center">
                        <p class="text-muted">No hay noticias disponibles en este momento.</p>
                    </div>
                `;
                return;
            }

            // Generar HTML de forma segura
            const html = data.map(item => createNoticiaHTML(item)).join('');
            container.innerHTML = html;

            console.log(`✅ ${data.length} noticias cargadas exitosamente`);
        } catch (error) {
            console.error('Error al cargar noticias:', error);
            container.innerHTML = `
                <div class="col-12 text-center">
                    <p class="text-danger">No se pudieron cargar las noticias. Por favor, intente nuevamente más tarde.</p>
                </div>
            `;
        }
    }

    // ==================== INICIALIZACIÓN ====================

    /**
     * Inicializa la aplicación cuando el DOM está listo
     */
    function init() {
        console.log('🚀 Inicializando aplicación Sintracajamag...');
        
        // Cargar directivos si el contenedor existe
        if (document.getElementById('swiper-wrapper')) {
            // Esperar a que el DOM esté completamente cargado
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', loadDirectivos);
            } else {
                loadDirectivos();
            }
        }

        // Cargar eventos si el contenedor existe
        if (document.querySelector('#eventos .row')) {
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', loadEventos);
            } else {
                loadEventos();
            }
        }

        // Cargar comunicados si el contenedor existe
        if (document.querySelector('#services .row')) {
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', loadComunicados);
            } else {
                loadComunicados();
            }
        }

        // Cargar noticias si el contenedor existe
        if (document.querySelector('#noticias .row')) {
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', loadNoticias);
            } else {
                loadNoticias();
            }
        }

        // Ajustar enlaces según el ambiente
        adjustEnvironmentLinks();
    }

    /**
     * Ajusta los enlaces según el ambiente (desarrollo o producción)
     */
    function adjustEnvironmentLinks() {
        const isLocal = document.location.hostname === '127.0.0.1' || 
                       document.location.hostname === 'localhost';
        
        if (isLocal) {
            // En desarrollo, redirigir el enlace de administración a localhost
            const adminLink = document.querySelector('.admin-link');
            if (adminLink) {
                adminLink.href = 'http://localhost:5173';
                console.log('Ambiente local detectado: Enlaces ajustados para desarrollo');
            }
        }
    }

    // Ejecutar inicialización
    init();

    // Exponer funciones necesarias globalmente de forma controlada
    window.SintracajamagApp = {
        loadDirectivos,
        loadEventos,
        loadComunicados,
        loadNoticias,
        mostrarEvento,
        mostrarComunicado,
        mostrarNoticia,
        clearCache: () => cache.clear()
    };

})();